import React, { useCallback, useContext, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi } from "ag-grid-community";
import { GridReadyEvent } from "ag-grid-community";
import { Grid, Tooltip, Fab } from "@mui/material";

import AgGridService from "../../../../../../shared/services/ag-grid/ag-grid.service";
import useHelpers from "../../../../../../shared/services/helpers/useHelpers.hook";
import useReportingAndAnalyticsApi from "../../../useReportingAndAnalyticsApi";
import useLoader from "../../../../../../shared/components/loading-dialog/useLoader.hook";
import LoadingDialogComponent from "../../../../../../shared/components/loading-dialog/loading-dialog.component";

import MuiIcon from "../../../../../../shared/styles/mui/helpers/mui-icon.helper";
import IColumnAndFunction from "../../../../../../shared/contracts/IColumnAndFunction";
import useAvailableColumnsListService from "./available-columns-list.component.service";
import { GROUP_FUNCTION } from "../../../../../../shared/contracts/TGroupFunctions";
import { CreateReportFormContext } from "../../../../../../shared/context/create-report/create-report-context";

interface IProps {}

function AvailableColumnsListComponent({}: IProps) {
  const { getKeyByValue } = useHelpers();
  const { hasDifference } = useHelpers();
  
  const { getDefaultColDef, getDefaultProps } = new AgGridService();
  const [gridApi, setGridApi] = useState<GridApi<IColumnAndFunction>>();
  const { createReportForm, dispatch } = useContext(CreateReportFormContext)!;

  const { getColDefns, insertRow, initializeRows } =
    useAvailableColumnsListService();
  const { getAvailableColumns } = useReportingAndAnalyticsApi();

  const [reloadRequired, setReloadRequired] = useState<boolean>(false);

  useEffect(() => {
    availableColumnsLoader.loadAsync();
  }, []);

  const [colDefs, setColDefs] = useState<ColDef<IColumnAndFunction>[]>([]);

  useEffect(() => {
    initializeRows(
      gridApi,
      createReportForm.reportSpecification.columnSelections
    );
  }, [gridApi]);

  const handleGridReady = (e: GridReadyEvent<IColumnAndFunction>) => {
    setGridApi(e.api);
  };

  const availableColumnsLoader = useLoader({
    loaderName: "Available Columns",
    loadAsync: () =>
      getAvailableColumns(
        {
          dataSourceName:
            createReportForm?.selectedDataSource?.dataSourceViewName.trim() ??
            "",
        },
        {
          doIf: () =>
            availableColumnsLoader.isNotLoading &&
            createReportForm.selectedDataSource != null &&
            createReportForm.reportSpecification?.columnSelections.length === 0,
          doBefore: () => {
            availableColumnsLoader.SET_LOADING("Fetching Available columns...");
            dispatch({
              type: "UPDATE",
              payload: {
                currentAvailableColumns: [],
              },
            });
          },
          onFulfilled: (data) => {
            dispatch({
              type: "UPDATE",
              payload: {
                currentAvailableColumns: [...data.columns],
              },
            });
            availableColumnsLoader.SET_SUCCESS("Success.!");
          },
          doAfter: () => setReloadRequired(false),
          onException: availableColumnsLoader.SET_EXCEPTION,
        }
      ),
  });

  return (
    <>
      <LoadingDialogComponent loaderHook={availableColumnsLoader} />
      <Grid item container direction="column" wrap="nowrap">
        <Grid container direction="row" wrap="nowrap" paddingBottom={4}>
          {/* The AG Grid component */}
          <div
            className={"ag-theme-quartz"}
            style={{ minHeight: "40vh", width: "100%" }}
          >
            <AgGridReact<IColumnAndFunction>
              //rowData={}
              columnDefs={getColDefns}
              defaultColDef={getDefaultColDef()}
              onGridReady={handleGridReady}
              {...getDefaultProps({
                rowHeight: 44,
                hideSideBar: true,
                rowDragManaged: true,
                autoSizeStrategy: { type: "fitGridWidth" },
              })}
            />
          </div>
        </Grid>
        <Grid
          item
          container
          direction="row"
          justifyContent="end"
          alignItems="center"
        >
          {/* Add Button */}
          <Tooltip title="Add New Column" placement="top-end">
            <Fab color="info" onClick={() => insertRow(gridApi)}>
              {MuiIcon.ADD()}
            </Fab>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
}
export default AvailableColumnsListComponent;
